import { LegalLayout, LegalList, LegalSection } from '../../ui/LegalLayout.js'

/**
 * The agreement an account holder is under.
 *
 * Kept to what the product actually enforces, which is the discipline that
 * matters here: a term the system does not enforce is a term nobody is held to,
 * and listing it teaches readers that none of the others are real either. The
 * acceptable-use rules below are the ones that genuinely end an account,
 * because sending to people who did not ask for it is what gets a shared
 * sending reputation destroyed for every other tenant at once.
 *
 * Nothing here describes billing. There is no payment in this product — see
 * `PlanPage` — so a payment clause would be describing a system that does not
 * exist. It goes in when billing does.
 */
export function TermsPage() {
  return (
    <LegalLayout title="Terms of service" updated="29 October 2026">
      <LegalSection heading="The agreement">
        <p>
          These terms govern your use of Carbon Mail, operated by CarbonWeb. By creating an account
          or sending through the service, you accept them. If you are agreeing on behalf of a
          company, you confirm you may bind that company.
        </p>
      </LegalSection>

      <LegalSection heading="Your account">
        <LegalList>
          <li>
            You are responsible for what happens under your account, including what anyone you
            invite to your organisation does.
          </li>
          <li>Keep your sign-in details to yourself, and tell us if you think they are known.</li>
          <li>You must be old enough to enter a contract where you live.</li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="What you may send">
        <p>
          This is the part that matters most, and the part we enforce. Sending to people who did not
          ask for it damages the deliverability of everyone sending through the same infrastructure,
          which is why the consequence is losing the account rather than a warning.
        </p>
        <LegalList>
          <li>
            <strong>Only send to people who agreed to hear from you.</strong> Purchased, scraped,
            rented or appended lists are not permitted, and neither is an address harvested from a
            website.
          </li>
          <li>
            <strong>Honour unsubscribes.</strong> Every marketing message carries a one-click
            unsubscribe and suppression takes effect immediately. Do not attempt to re-add a
            suppressed address, and do not use the service to work around one.
          </li>
          <li>
            <strong>Be identifiable.</strong> Do not forge headers, disguise who is sending, or
            impersonate another person or organisation.
          </li>
          <li>No malware, phishing, or content that is unlawful where it is sent or received.</li>
          <li>
            You must comply with the law that applies to your sending, which depending on where you
            and your recipients are may include GDPR, the UK GDPR, CAN-SPAM, CASL or PECR.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Your data and your contacts">
        <LegalList>
          <li>
            Your contacts and your message content remain yours. We claim no ownership of them.
          </li>
          <li>
            You confirm you have permission to hold and email every address you upload, and that you
            can evidence it.
          </li>
          <li>
            We process contact data on your instruction, as a processor. The{' '}
            <strong>privacy notice</strong> says exactly what that means and who else sees it.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Limits and the provider you connect">
        <LegalList>
          <li>
            Carbon Mail sends through whichever provider you connect. That provider sets its own
            sending limits and its own rules, and we cannot send beyond them.
          </li>
          <li>
            Some providers will only send as one fixed address regardless of what a campaign says.
            Where that applies, the review screen says so before you send.
          </li>
          <li>
            We apply rate limits to protect shared deliverability. A send may therefore take longer
            than the raw size of the audience suggests.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Suspension">
        <p>
          We may suspend or close an account that breaches the sending rules above, that generates a
          complaint or bounce rate high enough to threaten delivery for others, or that is being
          used unlawfully. Where it is safe and lawful to warn first, we will.
        </p>
      </LegalSection>

      <LegalSection heading="Availability and liability">
        <LegalList>
          <li>
            The service is provided as it is. We do not promise it will be uninterrupted or
            error-free, and we do not guarantee that any particular message will be delivered —
            delivery is ultimately decided by the receiving mail server.
          </li>
          <li>
            We are not liable for lost profits, lost business or indirect loss. Nothing here
            excludes liability that cannot lawfully be excluded.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Ending it">
        <LegalList>
          <li>You may close your account at any time.</li>
          <li>
            On closure we delete your data on request, except suppression records and send logs,
            which are kept so that people who unsubscribed stay unsubscribed and so that what was
            sent remains evidenced. The privacy notice explains why.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection heading="Changes, and how to reach us">
        <p>
          If these terms change materially we will say so on this page and change the date at the
          top. Continuing to use the service after that means accepting the change.
        </p>
        <p>
          Questions: <strong>akhan@carbonweb.co</strong>.
        </p>
      </LegalSection>
    </LegalLayout>
  )
}
